import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Trash } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	AccordionTrigger,
} from "@/components/Accordion";
import {
	Alert,
	AlertActions,
	AlertDescription,
	AlertTitle,
} from "@/components/catalyst/Alert";
import { Button } from "@/components/catalyst/Button";
import { orpc } from "@/orpc/client";

export function ArchiveAppSection({
	appId,
	appName,
	isArchivable,
}: {
	appId: string;
	appName: string;
	isArchivable: boolean;
}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [isOpen, setIsOpen] = useState(false);

	const archiveMutation = useMutation(
		orpc.apps.archive.mutationOptions({
			onSuccess: () => {
				toast.success("App archived successfully");
				queryClient.invalidateQueries({
					queryKey: orpc.apps.list.queryKey(),
				});
				navigate({ to: "/dashboard" });
			},
		}),
	);

	return (
		<>
			<AccordionItem value="archive-danger">
				<AccordionHeader>
					<AccordionTrigger className="data-[state=open]:rounded-t-lg">
						<div className="flex items-center gap-3">
							<div className="flex size-8 items-center justify-center rounded-lg bg-error/10 text-error">
								<Trash className="size-4" />
							</div>
							<div className="flex flex-col items-start">
								<span className="font-semibold text-error">Danger Zone</span>
							</div>
						</div>
					</AccordionTrigger>
				</AccordionHeader>
				<AccordionPanel className="rounded-b-lg">
					<div className="flex flex-col gap-4 pt-2">
						<div className="flex items-center justify-between gap-4 rounded-lg border border-warning/20 bg-warning/5 p-4">
							<div className="flex flex-col gap-1">
								<span className="text-sm font-medium text-warning">
									Archive this app
								</span>
								<span className="text-xs text-warning/80">
									{isArchivable
										? "Once you archive an app, it will be hidden from your dashboard."
										: "Cannot archive while referral links have active obligations."}
								</span>
							</div>
							<Button
								color="amber"
								onClick={() => setIsOpen(true)}
								disabled={!isArchivable}
							>
								Archive App
							</Button>
						</div>
					</div>
				</AccordionPanel>
			</AccordionItem>

			<Alert open={isOpen} onClose={setIsOpen}>
				<AlertTitle>Are you absolutely sure?</AlertTitle>
				<AlertDescription>
					This action cannot be undone. This will permanently archive the app
					<span className="font-semibold"> {appName} </span>
					and remove it from your view.
				</AlertDescription>
				<AlertActions>
					<Button plain onClick={() => setIsOpen(false)}>
						Cancel
					</Button>
					<Button
						color="amber"
						onClick={() => archiveMutation.mutate({ id: appId })}
						disabled={archiveMutation.isPending}
					>
						{archiveMutation.isPending ? "Archiving..." : "Archive App"}
					</Button>
				</AlertActions>
			</Alert>
		</>
	);
}
